import { useState, useEffect } from 'react';
import { Upload, Image as ImageIcon, Truck } from 'lucide-react';
import { toast } from 'sonner';
import { api, fileUrl } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

function DayPicker({ selected, onToggle, testid }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {DAYS.map((d) => (
        <label key={d} className={`px-3 py-1.5 rounded-full border cursor-pointer text-sm font-manrope ${selected.includes(d) ? 'border-forest bg-forest text-cream' : 'border-forest/20 bg-white text-dark'}`}>
          <Checkbox className="hidden" checked={selected.includes(d)} onCheckedChange={() => onToggle(d)} />{d.slice(0, 3)}
        </label>
      ))}
    </div>
  );
}

function BusinessInfoTab() {
  const [profile, setProfile] = useState({ story: '', town: '', website: '', practices: '', logo_file_id: null, cover_file_id: null, photo_file_ids: [] });
  const [status, setStatus] = useState('');
  const [uploading, setUploading] = useState('');

  useEffect(() => { api.get('/farm/profile').then((r) => { setProfile({ ...profile, ...r.data.profile }); setStatus(r.data.profile_status); }).catch(() => {}); /* eslint-disable-next-line */ }, []);

  const set = (k, v) => setProfile((p) => ({ ...p, [k]: v }));

  const upload = async (e, key) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(key);
    const fd = new FormData(); fd.append('file', file);
    try { const r = await api.post('/farm/upload-image', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); set(key, r.data.file_id); toast.success('Image uploaded'); }
    catch { toast.error('Upload failed'); } finally { setUploading(''); e.target.value = ''; }
  };

  const save = async () => {
    try { await api.put('/farm/profile', { profile }); toast.success('Profile submitted for FarmBox approval'); setStatus('Submitted'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div data-testid="farm-profile-business">
      <div className="flex items-center gap-3 mb-6">
        <p className="font-manrope text-dark/60">Profile changes require FarmBox approval before appearing publicly.</p>
        <span className={`px-3 py-1 rounded-full text-xs font-bold ${status?.startsWith('Approved') ? 'bg-forest/10 text-forest' : 'bg-gold/15 text-gold-dark'}`}>{status}</span>
      </div>
      <div className="bg-white rounded-2xl p-6 md:p-8 border border-forest/10 space-y-5">
        <div className="grid sm:grid-cols-2 gap-5">
          {['logo', 'cover'].map((key) => (
            <div key={key}>
              <Label className="capitalize">{key} image</Label>
              <div className="mt-2 h-40 rounded-xl bg-cream border border-forest/20 overflow-hidden flex items-center justify-center relative">
                {profile[`${key}_file_id`] ? <img src={fileUrl(profile[`${key}_file_id`])} alt={key} className="w-full h-full object-cover" /> : <ImageIcon className="w-10 h-10 text-dark/20" />}
              </div>
              <label className="btn-secondary py-2 px-4 text-sm mt-2 cursor-pointer inline-flex" data-testid={`upload-${key}`}><Upload className="w-4 h-4 mr-2" />{uploading === `${key}_file_id` ? 'Uploading…' : 'Upload'}<input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e, `${key}_file_id`)} /></label>
            </div>
          ))}
        </div>
        <div><Label>Farm story</Label><Textarea className="bg-cream border-forest/20" rows={4} value={profile.story} onChange={(e) => set('story', e.target.value)} data-testid="profile-story" /></div>
        <div className="grid sm:grid-cols-2 gap-5">
          <div><Label>Town</Label><Input className="bg-cream border-forest/20" value={profile.town} onChange={(e) => set('town', e.target.value)} data-testid="profile-town" /></div>
          <div><Label>Website</Label><Input className="bg-cream border-forest/20" value={profile.website} onChange={(e) => set('website', e.target.value)} /></div>
        </div>
        <div><Label>Farming practices (comma separated)</Label><Input className="bg-cream border-forest/20" placeholder="Grass-fed, Organic, No pesticides" value={profile.practices} onChange={(e) => set('practices', e.target.value)} /></div>
        <button onClick={save} className="btn-primary w-full justify-center" data-testid="save-profile">Submit for Approval</button>
      </div>
    </div>
  );
}

function PickupPreferencesTab() {
  const [prefs, setPrefs] = useState({ days: [], start_time: '', end_time: '', notes: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get('/farm/pickup-preferences').then((r) => setPrefs((p) => ({ ...p, ...r.data }))).catch(() => {}); }, []);

  const set = (k, v) => setPrefs((p) => ({ ...p, [k]: v }));
  const toggleDay = (d) => setPrefs((p) => ({ ...p, days: p.days.includes(d) ? p.days.filter((x) => x !== d) : [...p.days, d] }));

  const save = async () => {
    setLoading(true);
    try { await api.put('/farm/pickup-preferences', prefs); toast.success('Pickup preferences saved'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setLoading(false); }
  };

  return (
    <div data-testid="farm-pickup-preferences">
      <p className="font-manrope text-dark/60 mb-6">These are preferences only — FarmBox Admin retains final control over actual pickup scheduling.</p>
      <div className="bg-white rounded-2xl p-6 md:p-8 border border-forest/10 space-y-5">
        <div><Label className="mb-2 block">Preferred pickup day(s)</Label><DayPicker testid="pickup-days" selected={prefs.days} onToggle={toggleDay} /></div>
        <div className="grid sm:grid-cols-2 gap-5">
          <div><Label>Preferred start time</Label><Input type="time" className="bg-cream border-forest/20" value={prefs.start_time} onChange={(e) => set('start_time', e.target.value)} data-testid="pickup-start-time" /></div>
          <div><Label>Preferred end time</Label><Input type="time" className="bg-cream border-forest/20" value={prefs.end_time} onChange={(e) => set('end_time', e.target.value)} data-testid="pickup-end-time" /></div>
        </div>
        <div><Label>Pickup instructions / notes (optional)</Label><Textarea className="bg-cream border-forest/20" rows={3} value={prefs.notes} onChange={(e) => set('notes', e.target.value)} /></div>
        <button onClick={save} disabled={loading} className="btn-primary w-full justify-center" data-testid="save-pickup-preferences">{loading ? 'Saving…' : 'Save Pickup Preferences'}</button>
      </div>
    </div>
  );
}

function DeliveryCapabilityTab() {
  const [cap, setCap] = useState({ option: 'No', area_radius: '', min_order: '', delivery_fee: '', delivery_days: [], notes: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get('/farm/delivery-capability').then((r) => setCap((c) => ({ ...c, ...r.data }))).catch(() => {}); }, []);

  const set = (k, v) => setCap((c) => ({ ...c, [k]: v }));
  const toggleDay = (d) => setCap((c) => ({ ...c, delivery_days: c.delivery_days.includes(d) ? c.delivery_days.filter((x) => x !== d) : [...c.delivery_days, d] }));

  const save = async () => {
    setLoading(true);
    try { await api.put('/farm/delivery-capability', cap); toast.success('Delivery capability saved'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setLoading(false); }
  };

  const showExtra = cap.option === 'Yes' || cap.option === 'Sometimes';

  return (
    <div data-testid="farm-delivery-capability">
      <p className="font-manrope text-dark/60 mb-6">This does not automatically change FarmBox's fulfillment method — Admin must approve/use farm delivery on an order or supplier basis.</p>
      <div className="bg-white rounded-2xl p-6 md:p-8 border border-forest/10 space-y-5">
        <div>
          <Label className="mb-2 block flex items-center gap-2"><Truck className="w-4 h-4" />Are you able to deliver FarmBox orders instead of FarmBox picking them up?</Label>
          <div className="flex flex-wrap gap-2">
            {[['No', 'FarmBox pickup only'], ['Yes', 'Delivery may be available'], ['Sometimes', 'Sometimes / depends on order']].map(([val, label]) => (
              <label key={val} className={`px-4 py-2.5 rounded-full border cursor-pointer text-sm font-manrope ${cap.option === val ? 'border-forest bg-forest text-cream' : 'border-forest/20 bg-white text-dark'}`} data-testid={`delivery-option-${val.toLowerCase()}`}>
                <input type="radio" className="hidden" checked={cap.option === val} onChange={() => set('option', val)} />{label}
              </label>
            ))}
          </div>
        </div>
        {showExtra && (
          <div className="space-y-5 border-t border-forest/10 pt-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <div><Label>Delivery area / radius</Label><Input className="bg-cream border-forest/20" placeholder="e.g. 15 miles" value={cap.area_radius} onChange={(e) => set('area_radius', e.target.value)} /></div>
              <div><Label>Minimum order for delivery</Label><Input className="bg-cream border-forest/20" placeholder="e.g. $50" value={cap.min_order} onChange={(e) => set('min_order', e.target.value)} /></div>
            </div>
            <div><Label>Delivery fee, if applicable</Label><Input className="bg-cream border-forest/20" placeholder="e.g. $10 or free" value={cap.delivery_fee} onChange={(e) => set('delivery_fee', e.target.value)} /></div>
            <div><Label className="mb-2 block">Available delivery days</Label><DayPicker testid="delivery-days" selected={cap.delivery_days} onToggle={toggleDay} /></div>
            <div><Label>Additional delivery notes</Label><Textarea className="bg-cream border-forest/20" rows={3} value={cap.notes} onChange={(e) => set('notes', e.target.value)} /></div>
          </div>
        )}
        <button onClick={save} disabled={loading} className="btn-primary w-full justify-center" data-testid="save-delivery-capability">{loading ? 'Saving…' : 'Save Delivery Capability'}</button>
      </div>
    </div>
  );
}

export default function FarmProfile() {
  return (
    <div data-testid="farm-profile" className="max-w-3xl">
      <Tabs defaultValue="business" className="w-full">
        <TabsList className="bg-white border border-forest/10 p-1 rounded-full mb-6">
          <TabsTrigger value="business" className="rounded-full data-[state=active]:bg-forest data-[state=active]:text-cream" data-testid="profile-tab-business">Business Info</TabsTrigger>
          <TabsTrigger value="pickup" className="rounded-full data-[state=active]:bg-forest data-[state=active]:text-cream" data-testid="profile-tab-pickup">Pickup Preferences</TabsTrigger>
          <TabsTrigger value="delivery" className="rounded-full data-[state=active]:bg-forest data-[state=active]:text-cream" data-testid="profile-tab-delivery">Delivery Capability</TabsTrigger>
        </TabsList>
        <TabsContent value="business"><BusinessInfoTab /></TabsContent>
        <TabsContent value="pickup"><PickupPreferencesTab /></TabsContent>
        <TabsContent value="delivery"><DeliveryCapabilityTab /></TabsContent>
      </Tabs>
    </div>
  );
}
